import { map, distinctUntilChanged } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject, ReplaySubject } from "rxjs";

import { ApiService } from "./utilities/api.service";
import { JwtService } from "./utilities/jwt.service";

import { User } from "../../models";
import { environment } from "../../../../environments/environment";

declare const FB: any;

@Injectable({
  providedIn: "root",
})
export class UserService {
  private currentUserSubject = new BehaviorSubject<User>(new User());
  public currentUser = this.currentUserSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  private isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();

  constructor(private apiService: ApiService, private jwtService: JwtService) {
    // this.initializeFaceBookData()
  }

  public initializeFaceBookData() {
    FB.init({
      appId: environment.facebookConfig.appId,
      status: false,
      cookie: false,
      xfbml: false,
      version: "v4.0",
    });
  }

  // Verify JWT in local storage with server & load user's info.
  // This runs once on application startup.
  public populate() {
    // If JWT detected, attempt to get & store user's info
    if (this.jwtService.getToken()) {
      return this.apiService.get("/user").subscribe(
        (data) => this.setAuth(data.user),
        (err) => this.purgeAuth()
      );
    } else {
      // Remove any potential remnants of previous auth states
      this.purgeAuth();
    }
  }

  public setAuth(user: User) {
    // Save JWT sent from server in local storage
    this.jwtService.saveToken(user.token);
    // Set current user data into observable
    this.currentUserSubject.next(user);
    // Set isAuthenticated to true
    this.isAuthenticatedSubject.next(true);
  }

  public deleteUser() {
    // Set current user to an empty object
    this.currentUserSubject.next(new User());
  }

  public purgeAuth() {
    // Remove JWT from local storage
    this.jwtService.destroyToken();
    // Set current user to an empty object
    this.currentUserSubject.next(new User());
    // Set auth status to false
    this.isAuthenticatedSubject.next(false);
  }

  public logOut() {
    return this.apiService.post("/users/logout");
  }

  public attemptLocalAuth(type: string, credentials: any): Observable<User> {
    let route = type === "login" ? "/login" : "";
    return this.apiService.post("/users" + route, { user: credentials }).pipe(
      map((data) => {
        this.setAuth(data.user);
        return data;
      })
    );
  }

  public destroy(credentials) {
    return this.apiService.post("/users/delete", { user: credentials });
  }

  public getCurrentUser(): User {
    return this.currentUserSubject.value;
  }

  public getCurrentUser_(): Observable<User> {
    return this.currentUser;
  }

  // Update the user on the server (email, pass, etc)
  public update(user: User): Observable<User> {
    return this.apiService.put("/user", { user }).pipe(
      map((data) => {
        // Update the currentUser observable
        this.currentUserSubject.next(data.user);
        return data.user;
      })
    );
  }

  public attemptTwitterAuth() {
    window.open(
      environment.api_url + "/users/auth/twitter",
      "twitterAuthWindow",
      "location=1,status=1,scrollbars=1"
    );
  }

  public attemptGoogleAuth() {
    window.open(
      environment.api_url + "/users/auth/google",
      "googleAuthWindow",
      "location=1,status=1,scrollbars=1"
    );
  }

  public attemptFacebookAuth() {
    return new Promise((resolve, reject) => {
      FB.login(
        (result) => {
          if (result.authResponse) {
            return this.apiService
              .post("/users/auth/facebook", {
                access_token: result.authResponse.accessToken,
              })
              .toPromise()
              .then((data) => {
                this.setAuth(data.user);
                resolve(data);
              })
              .catch(() => reject());
          } else {
            reject();
          }
        },
        { scope: "public_profile,email" }
      );
    });
  }
}
